import { useState } from "react";
import { T, fontDisplay, fontMono } from "../theme";
import AmexGrid from "./AmexGrid";
import Paydowns from "./Paydowns";
import { fmtDay, fmtMoney, todayStr } from "../engine/dates";
import { addAmexPayment, deleteAmexPayment, saveAmexSettings, setPaydownOverride, updateAmexPayment } from "../lib/data";

/**
 * The card as a facility, not a cost line.
 *
 * Spend does not leave the bank when it is charged — it leaves when the card is
 * paid. The question a limit forces is not "when does it clear" but "when does
 * it run out of room, and what has to be paid to make some". £69,700 of media
 * cannot sit on a £35,000 card however evenly it is spread, and that is the
 * thing this view exists to say out loud.
 */
export default function AmexView({ settings, payments, amex, weeks = [], charges = [], docNames = {}, overdraftLimit, onChanged }) {
  const [form, setForm] = useState({
    credit_limit: settings?.credit_limit ?? "",
    opening_balance: settings?.opening_balance ?? "",
    opening_date: settings?.opening_date ?? todayStr(),
    statement_day: settings?.statement_day ?? 1,
    payment_offset_days: settings?.payment_offset_days ?? 25,
    auto_pay_statement: settings?.auto_pay_statement !== false,
    enforce_limit: !!settings?.enforce_limit,
  });
  const [pay, setPay] = useState({ payment_date: todayStr(), amount: "", note: "", paid: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const saveSettings = async () => {
    setBusy(true);
    setError(null);
    try {
      await saveAmexSettings({
        credit_limit: Number(form.credit_limit) || 0,
        opening_balance: Number(form.opening_balance) || 0,
        opening_date: form.opening_date,
        statement_day: Math.min(Math.max(Number(form.statement_day) || 1, 1), 28),
        payment_offset_days: Number(form.payment_offset_days) || 0,
        auto_pay_statement: form.auto_pay_statement,
        enforce_limit: form.enforce_limit,
      });
      await onChanged();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const addPayment = async () => {
    if (!Number(pay.amount)) return;
    setBusy(true);
    setError(null);
    try {
      await addAmexPayment({
        payment_date: pay.payment_date,
        amount: Number(pay.amount),
        note: pay.note || null,
        paid: pay.paid,
      });
      setPay({ payment_date: todayStr(), amount: "", note: "", paid: false });
      await onChanged();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const breaches = amex?.breaches || [];
  const firstBreach = breaches[0];
  const limit = Number(form.credit_limit) || 0;
  const peak = amex?.peak;
  const lbl = { fontSize: 11, color: T.muted };

  return (
    <div>
      <h2 style={{ fontFamily: fontDisplay, fontSize: 22, marginBottom: 4 }}>AMEX</h2>
      <div style={{ maxWidth: 940 }}>
      <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.6, maxWidth: 680 }}>
        Spend on the card doesn't leave the bank when it's charged — it leaves when the card is
        paid. This is the balance over time, when payments fall due, and whether the limit holds.
      </p>

      {amex?.usingDefaults && (
        <div style={{ fontSize: 12, color: T.warn, margin: "10px 0" }}>
          No settings saved yet, so a statement on the 1st paid 25 days later is assumed and no
          limit is enforced. Set the real numbers below.
        </div>
      )}

      {/* The headline: does the plan fit on the card at all. */}
      {limit > 0 && (
        <div
          style={{
            display: "flex",
            gap: 12,
            margin: "14px 0",
            flexWrap: "wrap",
          }}
        >
          <div style={{ background: T.panel, borderRadius: 10, padding: "14px 16px", flex: 1, minWidth: 190 }}>
            <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.8, color: T.muted }}>
              Peak balance forecast
            </div>
            <div
              style={{
                fontFamily: fontDisplay,
                fontSize: 28,
                color: peak && peak.balance > limit ? T.cost : T.ink,
              }}
            >
              {fmtMoney(peak?.balance || 0)}
            </div>
            <div style={{ fontSize: 11, color: T.muted }}>
              {peak?.date ? fmtDay(peak.date) : "—"} · limit {fmtMoney(limit)}
            </div>
          </div>
          <div style={{ background: T.panel, borderRadius: 10, padding: "14px 16px", flex: 1, minWidth: 190 }}>
            <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.8, color: T.muted }}>
              Headroom
            </div>
            <div
              style={{
                fontFamily: fontDisplay,
                fontSize: 28,
                color: (amex?.headroom ?? 0) < 0 ? T.cost : T.income,
              }}
            >
              {fmtMoney(amex?.headroom ?? limit)}
            </div>
            <div style={{ fontSize: 11, color: T.muted }}>
              {(amex?.headroom ?? 0) < 0 ? "over the limit at peak" : "spare at the worst point"}
            </div>
          </div>
        </div>
      )}

      {firstBreach && (
        <div
          style={{
            background: "#F7E9E9",
            border: `1px solid ${T.cost}`,
            borderRadius: 10,
            padding: 14,
            margin: "12px 0",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: T.cost }}>
            The card runs out of room on {fmtDay(firstBreach.date)}
          </div>
          <div style={{ fontSize: 12, color: T.ink, marginTop: 4, lineHeight: 1.5 }}>
            Planned spend takes the balance to {fmtMoney(firstBreach.balance)} against a{" "}
            {fmtMoney(firstBreach.limit)} limit — {fmtMoney(firstBreach.over)} more than the card
            will hold{firstBreach.label ? ` (${firstBreach.label})` : ""}. Either pay down at least
            that much before then, move some of the spend off the card, or spread it further out.
            {breaches.length > 1 && ` It stays over on ${breaches.length} occasions.`}
          </div>
        </div>
      )}

      {amex?.suggestions?.length > 0 && (
        <div style={{ background: T.panel, border: `1px solid ${T.warn}`, borderRadius: 10, padding: 14, margin: "12px 0" }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            What it would take to stay inside the limit
          </div>
          <div style={{ fontSize: 11, color: T.muted, marginBottom: 8, lineHeight: 1.5 }}>
            Proposals, not forecast lines — the card doesn't pay itself, and a suggestion that
            quietly became a payment would be inventing cash movements nobody agreed to. Add any of
            them and they behave like any other payment.
          </div>
          {amex.suggestions.map((x) => (
            <div
              key={x.date}
              style={{ display: "flex", gap: 10, alignItems: "center", padding: "5px 0", borderTop: `1px solid ${T.borderSoft}`, fontSize: 11 }}
            >
              <span style={{ fontFamily: fontMono, minWidth: 90 }}>{fmtDay(x.date)}</span>
              <span style={{ fontFamily: fontMono, color: T.cost, minWidth: 74 }}>{fmtMoney(x.amount)}</span>
              <span style={{ color: T.muted }}>{x.reason}</span>
              <button
                onClick={async () => {
                  // Agreeing to a suggested paydown sets the amount the limit
                  // rule should use, rather than creating a separate payment
                  // record beside it. Two mechanisms for one payment is how a
                  // £13,940 suggestion became a permanent line nobody could
                  // edit: the override screen could not see it, and the engine
                  // went on calculating its own paydown alongside.
                  setBusy(true);
                  await setPaydownOverride(x.date, { amount: x.amount, skip: false });
                  await onChanged();
                  setBusy(false);
                }}
                disabled={busy}
                style={{ marginLeft: "auto", border: "none", background: T.accent, color: "#fff", borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}
              >
                Agree this paydown
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ background: T.panel, borderRadius: 10, padding: 16, margin: "14px 0" }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>Card settings</div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label style={lbl}>
            Credit limit
            <br />
            <input
              value={form.credit_limit}
              onChange={(e) => setForm({ ...form, credit_limit: e.target.value })}
              placeholder="35000"
              style={{ fontFamily: fontMono, fontSize: 12, padding: "5px 6px", width: 100, marginTop: 2 }}
            />
          </label>
          <label style={lbl}>
            Balance now
            <br />
            <input
              value={form.opening_balance}
              onChange={(e) => setForm({ ...form, opening_balance: e.target.value })}
              placeholder="0"
              style={{ fontFamily: fontMono, fontSize: 12, padding: "5px 6px", width: 100, marginTop: 2 }}
            />
          </label>
          <label style={lbl}>
            as at
            <br />
            <input
              type="date"
              value={form.opening_date}
              onChange={(e) => setForm({ ...form, opening_date: e.target.value })}
              style={{ fontFamily: fontMono, fontSize: 12, padding: "5px 6px", marginTop: 2 }}
            />
          </label>
          <label style={lbl}>
            Statement day
            <br />
            <input
              type="number"
              min="1"
              max="28"
              value={form.statement_day}
              onChange={(e) => setForm({ ...form, statement_day: e.target.value })}
              style={{ fontFamily: fontMono, fontSize: 12, padding: "5px 6px", width: 60, marginTop: 2 }}
            />
          </label>
          <label style={lbl}>
            Paid after (days)
            <br />
            <input
              type="number"
              min="0"
              max="60"
              value={form.payment_offset_days}
              onChange={(e) => setForm({ ...form, payment_offset_days: e.target.value })}
              style={{ fontFamily: fontMono, fontSize: 12, padding: "5px 6px", width: 60, marginTop: 2 }}
            />
          </label>
          <label style={{ ...lbl, display: "flex", alignItems: "center", gap: 5 }}>
            <input
              type="checkbox"
              checked={form.auto_pay_statement}
              onChange={(e) => setForm({ ...form, auto_pay_statement: e.target.checked })}
            />
            cleared in full each statement
          </label>
          <label style={{ ...lbl, display: "flex", alignItems: "center", gap: 5 }}>
            <input
              type="checkbox"
              checked={form.enforce_limit}
              onChange={(e) => setForm({ ...form, enforce_limit: e.target.checked })}
            />
            pay it down to stay inside the limit
          </label>
          <button
            onClick={saveSettings}
            disabled={busy}
            style={{
              padding: "7px 16px",
              borderRadius: 6,
              border: "none",
              background: T.accent,
              color: "#fff",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            Save
          </button>
        </div>
        <div style={{ fontSize: 10, color: T.muted, marginTop: 8, lineHeight: 1.5 }}>
          Untick "cleared in full" if the card carries a balance — then only the payments you
          record below reduce it, which is what a limit usually forces.
        </div>
      </div>

      <div style={{ background: T.panel, borderRadius: 10, padding: 16, marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>Record a payment to the card</div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label style={lbl}>
            Date
            <br />
            <input
              type="date"
              value={pay.payment_date}
              onChange={(e) => setPay({ ...pay, payment_date: e.target.value })}
              style={{ fontFamily: fontMono, fontSize: 12, padding: "5px 6px", marginTop: 2 }}
            />
          </label>
          <label style={lbl}>
            Amount
            <br />
            <input
              value={pay.amount}
              onChange={(e) => setPay({ ...pay, amount: e.target.value })}
              placeholder="10000"
              style={{ fontFamily: fontMono, fontSize: 12, padding: "5px 6px", width: 100, marginTop: 2 }}
            />
          </label>
          <label style={{ ...lbl, flex: 1, minWidth: 150 }}>
            Note
            <br />
            <input
              value={pay.note}
              onChange={(e) => setPay({ ...pay, note: e.target.value })}
              placeholder="paydown before media burst"
              style={{ fontSize: 12, padding: "5px 6px", width: "100%", marginTop: 2 }}
            />
          </label>
          <label style={{ ...lbl, display: "flex", alignItems: "center", gap: 5 }}>
            <input
              type="checkbox"
              checked={pay.paid}
              onChange={(e) => setPay({ ...pay, paid: e.target.checked })}
            />
            already paid
          </label>
          <button
            onClick={addPayment}
            disabled={busy || !Number(pay.amount)}
            style={{
              padding: "7px 16px",
              borderRadius: 6,
              border: "none",
              background: Number(pay.amount) ? T.accent : T.border,
              color: Number(pay.amount) ? "#fff" : T.muted,
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            Add payment
          </button>
        </div>

        {payments?.length > 0 && (
          <div style={{ marginTop: 12 }}>
            {payments.map((p) => (
              <div
                key={p.id}
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "center",
                  padding: "6px 0",
                  borderTop: `1px solid ${T.borderSoft}`,
                  fontSize: 11,
                }}
              >
                <span style={{ fontFamily: fontMono, minWidth: 90 }}>{fmtDay(p.payment_date)}</span>
                <span style={{ fontFamily: fontMono, color: p.paid ? T.settled : T.cost, minWidth: 70 }}>
                  {fmtMoney(-p.amount)}
                </span>
                <span style={{ color: T.muted }}>{p.note}</span>
                <label style={{ marginLeft: "auto", display: "flex", gap: 5, alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={!!p.paid}
                    disabled={busy}
                    onChange={async (e) => {
                      setBusy(true);
                      await updateAmexPayment(p.id, { paid: e.target.checked });
                      await onChanged();
                      setBusy(false);
                    }}
                  />
                  already paid
                </label>
                <button
                  onClick={async () => {
                    setBusy(true);
                    await deleteAmexPayment(p.id);
                    await onChanged();
                    setBusy(false);
                  }}
                  style={{ border: "none", background: "none", color: T.muted, cursor: "pointer", fontSize: 14 }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && <div style={{ color: T.cost, fontSize: 12, marginBottom: 10 }}>{error}</div>}


      </div>

      {amex?.enforceLimit && (
        <div
          style={{
            background: "#FBEFE2",
            border: `1px solid ${T.warn}`,
            borderRadius: 8,
            padding: 12,
            margin: "14px 0",
            fontSize: 12,
            lineHeight: 1.6,
            maxWidth: 940,
          }}
        >
          <strong>The card is being held inside its limit.</strong> A real card declines the next
          charge rather than letting the balance run past the limit and settling up later, so the
          forecast pays it down on the day it would have gone over.{" "}
          {(() => {
            const forced = (amex.payments || []).filter((p) => p.enforced);
            const total = forced.reduce((s, p) => s + p.amount, 0);
            return forced.length ? (
              <>
                That is <strong>{fmtMoney(total)}</strong> across {forced.length} payments, on top
                of the statements — cash that has to come from somewhere, and it is in the forecast.
              </>
            ) : null;
          })()}{" "}
          Turn it off in the settings above to see what the balance would do if nobody intervened.
        </div>
      )}

      <h3 style={{ fontFamily: fontDisplay, fontSize: 17, margin: "18px 0 4px" }}>Week by week</h3>
      <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.6, maxWidth: 680, margin: 0 }}>
        The same shape as the cash flow, for the card instead of the bank. What goes on it, what
        comes off it, and what is left owing — measured against the limit rather than an overdraft.
      </p>
      <AmexGrid weeks={weeks} charges={charges} amex={amex} docNames={docNames} />

      <Paydowns
        paydowns={amex?.forcedPaydowns || []}
        weeks={weeks}
        overdraftLimit={overdraftLimit}
        onChanged={onChanged}
      />

      <h3 style={{ fontFamily: fontDisplay, fontSize: 17, margin: "22px 0 6px" }}>Every movement</h3>
      <div style={{ overflowX: "auto", background: T.panel, borderRadius: 10 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          <thead>
            <tr style={{ color: T.muted, textAlign: "left" }}>
              <th style={{ padding: "8px 12px" }}>Date</th>
              <th style={{ padding: "8px 12px" }}>What</th>
              <th style={{ padding: "8px 12px", textAlign: "right" }}>Amount</th>
              <th style={{ padding: "8px 12px", textAlign: "right" }}>Balance</th>
            </tr>
          </thead>
          <tbody>
            {(amex?.timeline || []).map((t, i) => {
              const over = limit > 0 && t.balance > limit;
              return (
                <tr
                  key={i}
                  style={{
                    borderTop: `1px solid ${T.borderSoft}`,
                    background: over ? "#F7E9E9" : undefined,
                  }}
                >
                  <td style={{ padding: "6px 12px", fontFamily: fontMono, color: T.muted }}>
                    {fmtDay(t.date)}
                  </td>
                  <td style={{ padding: "6px 12px" }}>
                    {t.kind === "opening"
                      ? "Opening balance"
                      : t.kind === "statement"
                        ? "Statement closed — balance becomes due"
                        : t.kind === "payment"
                          ? `Payment${t.label ? ` · ${t.label}` : ""}`
                          : t.label || "Charge"}
                  </td>
                  <td style={{ padding: "6px 12px", textAlign: "right", fontFamily: fontMono, color: t.kind === "payment" ? T.income : T.muted }}>
                    {t.amount ? `${t.kind === "payment" ? "-" : "+"}${fmtMoney(t.amount)}` : ""}
                  </td>
                  <td
                    style={{
                      padding: "6px 12px",
                      textAlign: "right",
                      fontFamily: fontMono,
                      fontWeight: 600,
                      color: over ? T.cost : T.ink,
                    }}
                  >
                    {fmtMoney(t.balance)}
                    {over && <span style={{ fontSize: 9, marginLeft: 5 }}>OVER</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
